import { ManifestBuilder } from "@radixdlt/radix-engine-toolkit"

import { CLAIM_COMPONENT_ADDRESS, CLAIM_NFT_ADDRESS, DEFAULT_PAIR_FEE, DEFAULT_PAIR_FILTER, DEFAULT_PAIR_K_IN, DEFAULT_PAIR_K_OUT, DEX_COMPONENT_ADDRESS, DEX_PACKAGE_ADDRESS, DFP2_BRIDGE_WALLET, MINIMUM_DFP2_AMOUNT, OLYMPIA_QUOTE_TOKEN_RESOURCE_ADDRESS, QUOTE_TOKEN_RESOURCE_ADDRESS, XRD_RESOURCE_ADDRESS } from "./util"
import { migrateComponents, migrateLiquidity } from "./migrate"
import { SelectToken } from "./models/tokens"
import { SelectPair } from "./models/pair"


export const createToken = (address: string) => ({
    fungible: ({
        name,
        symbol,
        description,
        iconUrl,
        initialSupply,
        badgeAddress
    }: Partial<{
        iconUrl: string
        name: string
        description: string
        symbol: string
        initialSupply: number
        badgeAddress: number
    }>) => `
CALL_METHOD
  Address("${address}")
    "create_proof_of_non_fungibles"
    Address("${badgeAddress}")
    Array<NonFungibleLocalId>(
        NonFungibleLocalId("#1#")
    )
;

CREATE_FUNGIBLE_RESOURCE_WITH_INITIAL_SUPPLY
    # Owner role - This gets metadata permissions, and is the default for other permissions
    # Can set as Enum<OwnerRole::Fixed>(access_rule)  or Enum<OwnerRole::Updatable>(access_rule)
    Enum<OwnerRole::Fixed>(
        Enum<2u8>(
            Enum<0u8>(
                Enum<0u8>(
                    Enum<0u8>(
                        NonFungibleGlobalId("${badgeAddress}:#1#")
                    )
                )
            )
        )
    )
    true             # Whether the engine should track supply (avoid for massively parallelizable tokens)
    18u8             # Divisibility (between 0u8 and 18u8)
    Decimal("${initialSupply}") # Initial supply
    Tuple(
        None,         # Mint Roles (if None: defaults to DenyAll, DenyAll)
        Some(         # Burn Roles (if None: defaults to DenyAll, DenyAll)
            Tuple(
                None,  # Burner (if None: defaults to Owner)
                None    # Burner Updater (if None: defaults to Owner)
            )
        ),       
        None,        # Freeze Roles (if None: defaults to DenyAll, DenyAll)
        None,        # Recall Roles (if None: defaults to DenyAll, DenyAll)
        None,        # Withdraw Roles (if None: defaults to AllowAll, DenyAll)
        None         # Deposit Roles (if None: defaults to AllowAll, DenyAll)
    )
    Tuple(                                                                   # Metadata initialization
        Map<String, Tuple>(                                                  # Initial metadata values
            ${[
            { key: 'name', value: name, type: 'String' },
            {
                key: 'symbol',
                value: symbol,
                type: 'String',
            },
            { key: 'description', value: description, type: 'String' },
            { key: 'icon_url', value: iconUrl, type: 'Url' },
        ]
            .filter(({ value }) => Boolean(value))
            .map(
                ({ key, value, type }) => `"${key}" => Tuple(
                Some(Enum<Metadata::${type}>("${value}")),    # Resource Name
                false                                         # Locked
            )`
            )
            .join(', ')}
        ),
        Map<String, Enum>()
    )
    None;             # No Address Reservation

# Depositing the entirety of the initial supply of the newly created resource into our account 
# component.
CALL_METHOD
    Address("${address}") 
    "deposit_batch"
    Expression("ENTIRE_WORKTOP")
;`,


    nft: ({
        name,
        description,
        iconUrl,
        items,
    }: {
        iconUrl: string
        name: string
        description: string
        items: { name: string; description: string; value: string }[]
    }) => `
  CREATE_NON_FUNGIBLE_RESOURCE_WITH_INITIAL_SUPPLY
    Enum<0u8>()
    Enum<1u8>()
    true
    Enum<NonFungibleDataSchema::Local>(
        Enum<0u8>(
            Tuple(
                Array<Enum>(
                    Enum<14u8>(
                        Array<Enum>(
                            Enum<0u8>(
                                12u8
                            ),
                            Enum<0u8>(
                                12u8
                            ),
                            Enum<1u8>(
                                1u64
                            ),
                            Enum<0u8>(
                                10u8
                            )
                        )
                    ),
                    Enum<12u8>()
                ),
                Array<Tuple>(
                    Tuple(
                        Enum<1u8>(
                            "MetadataStandardNonFungibleData"
                        ),
                        Enum<1u8>(
                            Enum<0u8>(
                                Array<String>(
                                    "name",
                                    "description",
                                    "key_image_url",
                                    "arbitrary_coolness_rating"
                                )
                            )
                        )
                    ),
                    Tuple(
                        Enum<1u8>(
                            "UncheckedUrl"
                        ),
                        Enum<0u8>()
                    )
                ),
                Array<Enum>(
                    Enum<0u8>(),
                    Enum<0u8>()
                )
            )
        ),
        Enum<1u8>(
            0u64
        ),
        Array<String>()
    )
    Map<NonFungibleLocalId, Tuple>(
        ${items
            .map(({ name, description, value }, index) => {
                return `NonFungibleLocalId("#${index}#") => Tuple(
            Tuple(
                "${name}",
                "${description}",
                "${value}",
                45u64
            )
        )`
            })
            .join(',')}
    )
    Tuple(
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>()
    )
    Tuple(
        Map<String, Tuple>(
            "description" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "${description}"
                    )
                ),
                true
            ),
            "icon_url" => Tuple(
                Enum<1u8>(
                    Enum<13u8>(
                        "${iconUrl}"
                    )
                ),
                true
            ),
            "info_url" => Tuple(
                Enum<1u8>(
                    Enum<13u8>(
                        "https://developers.radixdlt.com/ecosystem"
                    )
                ),
                true
            ),
            "name" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "${name}"
                    )
                ),
                true
            ),
            "tags" => Tuple(
                Enum<1u8>(
                    Enum<128u8>(
                        Array<String>(
                            "collection",
                            "sandbox",
                            "example-tag"
                        )
                    )
                ),
                true
            )
        ),
        Map<String, Enum>()
    )
    Enum<0u8>()
;
CALL_METHOD
    Address("${address}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP")
;`,
})

export function instantiateDex(address: string, packageAddress: string, dfp2Address: string, badgeAddress: string) {

    return `
CALL_FUNCTION
  Address("${packageAddress}")
  "PlazaDex"
  "instantiate_dex"
  Address("${dfp2Address}")
  Address("${badgeAddress}")
;
`
}

export function generateCreatePairManifest(account: string, tokenAddress: string, tokenAmount: string, p0: string, fee: number) {

    return `
CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${QUOTE_TOKEN_RESOURCE_ADDRESS}")
  Decimal("${MINIMUM_DFP2_AMOUNT}");

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${tokenAddress}")
  Decimal("${tokenAmount}");

TAKE_ALL_FROM_WORKTOP
  Address("${QUOTE_TOKEN_RESOURCE_ADDRESS}")
  Bucket("dfp2_bucket");

TAKE_ALL_FROM_WORKTOP
  Address("${tokenAddress}")
  Bucket("token_bucket");


` + generateCreatePairPart(p0, fee);
}

export function generateCreatePairPart(p0: string, fee: number) {
    return `
CALL_METHOD
	Address("${DEX_COMPONENT_ADDRESS}")
	"create_pair"
	Bucket("token_bucket")
	Bucket("dfp2_bucket")
	Tuple(
		Decimal("${DEFAULT_PAIR_K_IN}"),
		Decimal("${DEFAULT_PAIR_K_OUT}"),
		Decimal("${fee}"),
        Decimal("${DEFAULT_PAIR_FILTER}")
	)
	Decimal("${p0}")
;`
}

export function generateAddLiquidityManifest(account: string, tokenAddress: string, tokenAmount: string, coTokenAddress: string, coInputAmount: string, pair: SelectPair) {

    if (pair.quoteToken !== QUOTE_TOKEN_RESOURCE_ADDRESS && pair.quoteToken !== XRD_RESOURCE_ADDRESS) {
        return generateAddLiquidityManifestPair(account, tokenAddress, tokenAmount, coTokenAddress, coInputAmount, pair);
    }

    if (+coInputAmount === 0) {
        return `

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${tokenAddress}")
  Decimal("${tokenAmount}");

TAKE_ALL_FROM_WORKTOP
  Address("${tokenAddress}")
  Bucket("tokens");

CALL_METHOD
	Address("${pair.dexAddress}")
	"add_liquidity"
	Bucket("tokens")
    None
	Some(Address("${pair.baseToken}"));

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`
    }

    return `

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${tokenAddress}")
  Decimal("${tokenAmount}");

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${coTokenAddress}")
  Decimal("${coInputAmount}");

TAKE_ALL_FROM_WORKTOP
  Address("${tokenAddress}")
  Bucket("tokens");

TAKE_ALL_FROM_WORKTOP
  Address("${coTokenAddress}")
  Bucket("coTokens");

CALL_METHOD
	Address("${pair.dexAddress}")
	"add_liquidity"
	Bucket("tokens")
	Some(Bucket("coTokens"))
	Some(Address("${pair.baseToken}"));

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`
}

export function generateAddLiquidityManifestPair(account: string, tokenAddress: string, tokenAmount: string, coTokenAddress: string, coInputAmount: string, pair: SelectPair) {

    if (+coInputAmount === 0) {
        return `

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${tokenAddress}")
  Decimal("${tokenAmount}");

TAKE_ALL_FROM_WORKTOP
  Address("${tokenAddress}")
  Bucket("tokens");

CALL_METHOD
	Address("${pair.address}")
	"add_liquidity"
	Bucket("tokens")
    None;

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`
    }

    return `

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${tokenAddress}")
  Decimal("${tokenAmount}");

CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${coTokenAddress}")
  Decimal("${coInputAmount}");

TAKE_ALL_FROM_WORKTOP
  Address("${tokenAddress}")
  Bucket("tokens");

TAKE_ALL_FROM_WORKTOP
  Address("${coTokenAddress}")
  Bucket("coTokens");

CALL_METHOD
	Address("${pair.address}")
	"add_liquidity"
	Bucket("tokens")
	Some(Bucket("coTokens"));

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`
}

export function generateRemoveLiquidityManifest(account: string, tokenAddress: string, tokenAmount: string, isQuote: boolean, pair: SelectPair) {

    if (pair.quoteToken !== QUOTE_TOKEN_RESOURCE_ADDRESS && pair.quoteToken !== XRD_RESOURCE_ADDRESS) {
        return generateRemoveLiquidityManifestPair(account, tokenAddress, tokenAmount, isQuote, pair);
    }

    return `
CALL_METHOD
    Address("${account}")
    "withdraw"
    Address("${tokenAddress}")
    Decimal("${tokenAmount}");

TAKE_ALL_FROM_WORKTOP
    Address("${tokenAddress}")
    Bucket("tokens");

CALL_METHOD
    Address("${pair.dexAddress}")
	"remove_liquidity"
	Bucket("tokens")
;

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`
}

export function generateRemoveLiquidityManifestPair(account: string, tokenAddress: string, tokenAmount: string, isQuote: boolean, pair: SelectPair) {

    return `
CALL_METHOD
    Address("${account}")
    "withdraw"
    Address("${tokenAddress}")
    Decimal("${tokenAmount}");

TAKE_ALL_FROM_WORKTOP
    Address("${tokenAddress}")
    Bucket("tokens");

CALL_METHOD
    Address("${pair.address}")
	"remove_liquidity"
	Bucket("tokens")
    ${!!isQuote}
;

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`
}

export function generateArchiveLiquidityManifest(account: string, localId: string, baseTokenAddress: string, quoteTokenAddress: string, baseTokenAmount: string, quoteTokenAmount: string, dexAddress: string, pairAddress: string) {

    let manifest = `
CALL_METHOD
  Address("${account}")
    "create_proof_of_non_fungibles"
    Address("${'NFT_OWNER_BADGE_ADDRESS'}")
    Array<NonFungibleLocalId>(
        NonFungibleLocalId("${localId}")
    )
;

CALL_METHOD
	Address("${dexAddress}")
	"withdraw_owned_liquidity"
	Address("${pairAddress}")
;
`

    if (baseTokenAmount) {
        manifest += `
CALL_METHOD
    Address("${account}")
    "withdraw"
    Address("${baseTokenAddress}")
    Decimal("${baseTokenAmount}")
;`
    }

    if (quoteTokenAmount) {
        manifest += `

CALL_METHOD
    Address("${account}")
    "withdraw"
    Address("${quoteTokenAddress}")
    Decimal("${quoteTokenAmount}")
;`
    }

    manifest += `

TAKE_ALL_FROM_WORKTOP
    Address("${baseTokenAddress}")
    Bucket("quoteTokens");

TAKE_ALL_FROM_WORKTOP
    Address("${quoteTokenAddress}")
    Bucket("baseTokens");

CALL_METHOD
    Address("${dexAddress}")
	"remove_liquidity"
	Bucket("baseTokens")
;

CALL_METHOD
    Address("${dexAddress}")
	"remove_liquidity"
	Bucket("quoteTokens")
;

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP");`

    return manifest;
}

export function generateSwapManifest(account: string, tokenAddress: string, tokenAmount: number, counterTokenAddress: string) {

    return `
CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${tokenAddress}")
  Decimal("${tokenAmount}")
;

TAKE_ALL_FROM_WORKTOP
  Address("${tokenAddress}")
  Bucket("tokens")
;

CALL_METHOD
	Address("${DEX_COMPONENT_ADDRESS}")
	"swap"
	Bucket("tokens")
	Address("${counterTokenAddress}")
;

CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP")
;
`
}

export function generateBridgeManifest(account: string, tokenAmount: string) {

    return `
CALL_METHOD
  Address("${account}")
  "withdraw"
  Address("${QUOTE_TOKEN_RESOURCE_ADDRESS}")
  Decimal("${tokenAmount}")
;

CALL_METHOD
    Address("${DFP2_BRIDGE_WALLET}")
   "try_deposit_batch_or_abort"
    Expression("ENTIRE_WORKTOP")
    Enum<0u8>()
;
`
}


/*
CALL_METHOD
    Address("component_tdx_e_1crxxnrhc0ekeyjz2agv6d3vf50vswtq3wxujjnrsd06ae0m4fr227e")
    "create_pair"
    Address("resource_tdx_e_1tktu96g2zh6zek5xun648wtav29tvyaekcppp68dcyca96kg7nwcfw")
    Bucket("dfp2_bucket")
    Tuple(
        Decimal("0.4"),
        Decimal("1"),
        Decimal("0")
    )
    Decimal("1");
 */

export function generateUpdateDappDefinition(account: string, componentAddress: string, badgeAddress: string, localId: string) {
    return `
CALL_METHOD
  Address("${account}")
    "create_proof_of_non_fungibles"
    Address("${badgeAddress}")
    Array<NonFungibleLocalId>(
        NonFungibleLocalId("${localId}")
    )
;

SET_METADATA
    Address("${account}")
    "claimed_entities"
    Enum<136u8>(
        Array<Address>(
            Address("${componentAddress}"),
            Address("${QUOTE_TOKEN_RESOURCE_ADDRESS}")
        )
    )
;

SET_METADATA
    Address("${account}")
    "claimed_websites"
    Enum<Metadata::OriginArray>(
        Array<String>(
            "https://radix.defiplaza.net"
        )
    )
;

SET_METADATA
    Address("${account}")
    "icon_url"
    Enum<Metadata::Url>(
        "https://radix.defiplaza.net/assets/img/babylon/defiplaza-badge-dapp.png"
    )
;


SET_METADATA
    Address("${componentAddress}")
    "dapp_definition"
    Enum<8u8>(
        Address("${account}")
    )
;


`
}

export function generateMigrateManifest(account: string, tokenAmount: string) {
    return `CALL_METHOD
    Address("${account}")
    "withdraw"
    Address("${OLYMPIA_QUOTE_TOKEN_RESOURCE_ADDRESS}")
    Decimal("${tokenAmount}")
;
TAKE_FROM_WORKTOP
    Address("${OLYMPIA_QUOTE_TOKEN_RESOURCE_ADDRESS}")
    Decimal("${tokenAmount}")
    Bucket("old_token")
;
CALL_METHOD
    Address("component_rdx1cq20fv4mnse428euwpgffpukx5wzztakaz2nrv7hpjg4wua6dzh2jl")
    "swap"
    Bucket("old_token")
;
CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP")
;
`

}

export function generateAddStakeManifest(account: string, token: SelectToken, amount: string, stakingComponent: string) {
    return `
CALL_METHOD
	Address("${account}")
	"withdraw"
	Address("${token.address}")
	Decimal("${amount}")
;

TAKE_FROM_WORKTOP
	Address("${token.address}")
	Decimal("${amount}")
	Bucket("astrl_bucket")
;

CALL_METHOD
	Address("${stakingComponent}")
	"add_stake"
	Bucket("astrl_bucket")
;

CALL_METHOD 
	Address("${account}") 
	"deposit_batch" 
	Expression("ENTIRE_WORKTOP")
;`;
}

export function generateUnstakingManifest(account: string, token: SelectToken, amount: string, stakingComponent: string) {
    return `
CALL_METHOD
	Address("${account}")
	"withdraw"
	Address("${token.address}")
	Decimal("${amount}")
;

TAKE_FROM_WORKTOP
	Address("${token.address}")
	Decimal("${amount}")
	Bucket("astrl_bucket")
;

CALL_METHOD
	Address("${stakingComponent}")
	"remove_stake"
	Bucket("astrl_bucket")
;

CALL_METHOD 
	Address("${account}") 
	"deposit_batch" 
	Expression("ENTIRE_WORKTOP")
;`;
}

export function generateClaimManifest(account: string, localId: string, claims: { type: string; amount: number; collectedAmount: number; remaining: string; }[]) {
	let manifest = `
`;

	for (let claim of claims) {

		// Skip empty claims for TX cosst and royalties
		if (+claim.remaining == 0) {
			continue;
		}

		manifest += `
CALL_METHOD
	Address("${account}")
	"create_proof_of_non_fungibles"
	Address("${CLAIM_NFT_ADDRESS}")
	Array<NonFungibleLocalId>(
		 NonFungibleLocalId("${localId}")
	)
;

POP_FROM_AUTH_ZONE
	Proof("proof${claim.type}")
;

CALL_METHOD
	Address("${CLAIM_COMPONENT_ADDRESS}")
	"claim_rewards"
	${claim.type}u8
	Decimal("${claim.remaining}")
  	Proof("proof${claim.type}")
;`;
	}

	manifest += `
CALL_METHOD
	Address("${account}")
	"deposit_batch"
	Expression("ENTIRE_WORKTOP")
;
`

	return manifest;
}